import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import DOMPurify from 'dompurify'

import { type EnrichedOrganizationAISettings } from '~types'

const StyledLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    outline: '2px solid rgb(8, 148, 207)',
    outlineOffset: '2px',
  },
})

interface PrivacyPolicyMessageProps {
  orgAiSettings: EnrichedOrganizationAISettings
  fontFamily?: string
  fontSize?: string
  historyType?: 'search' | 'chat'
  isMobile?: boolean
}

export const PrivacyPolicyMessage: React.FC<PrivacyPolicyMessageProps> = ({
  orgAiSettings,
  fontFamily = 'inherit',
  fontSize = '14px',
  historyType = 'search',
  isMobile = false,
}) => {
  const historyText = historyType === 'chat' ? 'Chat history' : 'Search history'

  return (
    <Box
      sx={{
        textAlign: 'center',
        fontSize: fontSize,
        p: 1,
        padding: isMobile ? '0 24px' : '8px 24px',
      }}
    >
      {/* Disclaimer: Only shown if search history. Will use orgAiSettings.disclaimer_text if provided, otherwise default text */}
      {historyType === 'search' && (
        <Typography
          variant='body2'
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              orgAiSettings?.disclaimer_text ||
                'AI can make mistakes. Please confirm important information.',
              { ALLOWED_ATTR: ['href', 'target', 'rel'] },
            ),
          }}
          sx={{
            fontFamily: fontFamily,
            fontSize: fontSize,
          }}
        />
      )}
      {/* Built by Polimorphic: Only shown if privacy policy is hidden. Polimorphic will link to home page depending on hide_polimorphic_link */}
      {orgAiSettings?.hide_privacy_policy ? (
        <Typography
          variant='body2'
          sx={{
            fontFamily: fontFamily,
            fontSize: fontSize,
            lineHeight: isMobile ? 1.5 : undefined,
          }}
        >
          {orgAiSettings?.hide_polimorphic_link ? (
            <Typography
              component='span'
              variant='body2'
              sx={{
                fontSize: fontSize,
                fontWeight: 'normal',
                fontFamily: fontFamily,
                lineHeight: isMobile ? 1.5 : undefined,
              }}
            >
              Built by Polimorphic.
            </Typography>
          ) : (
            <>
              Built by{' '}
              <StyledLink
                href='https://polimorphic.com'
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  fontWeight: 'bold',
                  fontSize: fontSize,
                  letterSpacing: '0.25px',
                  fontFamily: fontFamily,
                }}
              >
                Polimorphic
              </StyledLink>
              .
            </>
          )}
        </Typography>
      ) : (
        <Typography
          variant='body2'
          sx={{
            fontFamily: fontFamily,
            fontSize: fontSize,
            lineHeight: isMobile ? 1.5 : undefined,
          }}
        >
          {/* Privacy Policy Text: Only shown if privacy policy is not hidden */}
          <Typography
            component='span'
            variant='body2'
            sx={{
              fontSize: fontSize,
              fontWeight: 'normal',
              fontFamily: fontFamily,
              lineHeight: isMobile ? 1.5 : undefined,
            }}
          >
            {historyText} is stored in accordance with our{' '}
            <StyledLink
              href='https://www.polimorphic.com/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              style={{
                fontWeight: 'bold',
                fontSize: fontSize,
                letterSpacing: '0.25px',
                fontFamily: fontFamily,
              }}
            >
              Privacy Policy
            </StyledLink>
            .{' '}
          </Typography>

          {/* By Polimorphic: only shown if privacy policy is not hidden. Polimorphic will link to home page depending on hide_polimorphic_link */}
          {orgAiSettings?.hide_polimorphic_link ? (
            <Typography
              component='span'
              variant='body2'
              sx={{
                fontSize: fontSize,
                fontWeight: 'normal',
                fontFamily: fontFamily,
                lineHeight: isMobile ? 1.5 : undefined,
              }}
            >
              By Polimorphic.
            </Typography>
          ) : (
            <>
              By{' '}
              <StyledLink
                href='https://polimorphic.com'
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  fontWeight: 'bold',
                  fontSize: fontSize,
                  letterSpacing: '0.25px',
                  fontFamily: fontFamily,
                }}
              >
                Polimorphic
              </StyledLink>
              .
            </>
          )}
        </Typography>
      )}
    </Box>
  )
}
